import PropTypes from 'prop-types';

import {
  TOKEN_COLORS,
  PROPERTIES,
  DERIVED_ACCESSORS,
  DERIVED_ACCESSOR_UNITS,
} from './constants';

// The identifier cheat-sheet above the editor: every slug the balance can
// reference, coloured to match how the editor highlights it.

const SymbolSummary = ({
  inputSlugs,
  outputSlugs,
  paramSlugs,
  inputUnits,
  outputUnits,
  paramUnits,
}) => {
  const inputEntries = inputSlugs.map((slug, idx) => ({
    slug,
    unit: inputUnits[idx],
    suffix: idx === 0 ? ' (anchor)' : '',
    color: TOKEN_COLORS.input,
  }));
  const outputEntries = outputSlugs.map((slug, idx) => ({
    slug,
    unit: outputUnits[idx],
    color: TOKEN_COLORS.output,
  }));
  const paramEntries = paramSlugs.map((slug, idx) => ({
    slug,
    unit: paramUnits[idx],
    color: TOKEN_COLORS.param,
  }));
  const propEntries = PROPERTIES.map(slug => ({ slug, color: TOKEN_COLORS.identifier }));
  const derivedEntries = DERIVED_ACCESSORS.map(slug => ({
    slug,
    unit: DERIVED_ACCESSOR_UNITS[slug],
    color: TOKEN_COLORS.identifier,
  }));

  return (
    <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2 mb-2 text-xs font-mono">
      <Row label="Inputs" entries={inputEntries} />
      <Row label="Outputs" entries={outputEntries} />
      <Row label="Params" entries={paramEntries} />
      <Row label="Species data" entries={propEntries} hint="read via &lt;io&gt;.&lt;property&gt;" />
      <Row label="Convert flow" entries={derivedEntries} hint="anchor + any assigned IO" />
    </div>
  );
};

SymbolSummary.propTypes = {
  inputSlugs: PropTypes.arrayOf(PropTypes.string).isRequired,
  outputSlugs: PropTypes.arrayOf(PropTypes.string).isRequired,
  paramSlugs: PropTypes.arrayOf(PropTypes.string).isRequired,
  inputUnits: PropTypes.arrayOf(PropTypes.string).isRequired,
  outputUnits: PropTypes.arrayOf(PropTypes.string).isRequired,
  paramUnits: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const Row = ({ label, entries, hint }) => (
  <div className="flex gap-2">
    <span className="w-24 shrink-0 text-gray-500">{label}:</span>
    <span className="text-gray-800">
      {entries.length ? (
        entries.map((e, idx) => (
          <span key={`${e.slug}-${idx}`}>
            <span className={e.color}>{e.slug}</span>
            {e.unit ? <span className="text-gray-400">{` (${e.unit})`}</span> : null}
            {e.suffix ? <span className="text-gray-400">{e.suffix}</span> : null}
            {idx < entries.length - 1 ? ', ' : ''}
          </span>
        ))
      ) : (
        <em className="text-gray-400">none</em>
      )}
      {hint ? <span className="text-gray-400 ml-2">— {hint}</span> : null}
    </span>
  </div>
);

Row.propTypes = {
  label: PropTypes.string.isRequired,
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      unit: PropTypes.string,
      suffix: PropTypes.string,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired,
  hint: PropTypes.string,
};

export default SymbolSummary;
